import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import { CPM_EXCISE_CATEGORY_LIST_LIMIT } from '../../constants/centralProductMaster.constants';
import { listExciseCategories, listStates } from '../../services/productMasterService';
import type { CpmExciseCategory, CpmState } from '../../types/centralProductMaster.types';

interface CpmExciseCategoryState {
    states: CpmState[];
    statesLoading: 'idle' | 'loading' | 'success' | 'error';
    statesError: string | null;
    selectedStateName: string | null;
    categories: CpmExciseCategory[];
    categoriesLoading: 'idle' | 'loading' | 'success' | 'error';
    categoriesError: string | null;
}

const initialState: CpmExciseCategoryState = {
    states: [],
    statesLoading: 'idle',
    statesError: null,
    selectedStateName: null,
    categories: [],
    categoriesLoading: 'idle',
    categoriesError: null,
};

export const fetchStates = createAsyncThunk(
    'cpmExciseCategory/fetchStates',
    async (_, { rejectWithValue }) => {
        try {
            return await listStates();
        } catch (err: unknown) {
            const message =
                err && typeof err === 'object' && 'message' in err
                    ? String((err as { message: string }).message)
                    : 'Failed to load states';
            return rejectWithValue(message);
        }
    }
);

export const fetchStateCategories = createAsyncThunk(
    'cpmExciseCategory/fetchStateCategories',
    async (stateName: string, { rejectWithValue }) => {
        try {
            const data = await listExciseCategories({
                state: stateName,
                page: 1,
                limit: CPM_EXCISE_CATEGORY_LIST_LIMIT,
            });
            return { stateName, categories: data.categories };
        } catch (err: unknown) {
            const message =
                err && typeof err === 'object' && 'message' in err
                    ? String((err as { message: string }).message)
                    : 'Failed to load excise categories';
            return rejectWithValue(message);
        }
    }
);

const cpmExciseCategorySlice = createSlice({
    name: 'cpmExciseCategory',
    initialState,
    reducers: {
        setSelectedStateName(state, action: PayloadAction<string | null>) {
            state.selectedStateName = action.payload;
            if (action.payload == null) {
                state.categories = [];
                state.categoriesLoading = 'idle';
                state.categoriesError = null;
            }
        },
        upsertCategory(state, action: PayloadAction<CpmExciseCategory>) {
            const incoming = action.payload;
            const idx = state.categories.findIndex((c) => c._id === incoming._id);
            if (idx < 0) return;
            const previous = state.categories[idx];
            state.categories[idx] = {
                ...previous,
                ...incoming,
                usageCount: incoming.usageCount ?? previous.usageCount,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStates.pending, (state) => {
                state.statesLoading = 'loading';
                state.statesError = null;
            })
            .addCase(fetchStates.fulfilled, (state, action) => {
                state.statesLoading = 'success';
                state.states = action.payload;
                state.statesError = null;
            })
            .addCase(fetchStates.rejected, (state, action) => {
                state.statesLoading = 'error';
                state.statesError = (action.payload as string) || 'Failed to load states';
            })
            .addCase(fetchStateCategories.pending, (state) => {
                state.categoriesLoading = 'loading';
                state.categoriesError = null;
            })
            .addCase(fetchStateCategories.fulfilled, (state, action) => {
                state.categoriesLoading = 'success';
                state.selectedStateName = action.payload.stateName;
                state.categories = action.payload.categories;
                state.categoriesError = null;
            })
            .addCase(fetchStateCategories.rejected, (state, action) => {
                state.categoriesLoading = 'error';
                state.categoriesError =
                    (action.payload as string) || 'Failed to load excise categories';
            });
    },
});

export const { setSelectedStateName, upsertCategory } = cpmExciseCategorySlice.actions;

export const useCpmExciseCategory = () => useSelector((s: RootState) => s.cpmExciseCategory);

export default cpmExciseCategorySlice.reducer;
